import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useClub } from '../hooks/useClub';

// PB101 kanban pilot rollout (BK, 9/18/26; expanded 9/29/26) — keep in
// sync with the same list in App.jsx and AzureApp/src/index.js
// (pb101-kanban route, the real enforcement point). FD/Manager get this
// only at these volunteer pilot clubs; Sales (Josh) is not gated by this
// list at all — see the isSales check below.
const PB101_KANBAN_PILOT_CLUBS = ['MCK', 'GB', 'MF', 'WA', 'LEW'];

export default function Sidebar() {
  const { isAdmin, isMloc, role } = useAuth();
  const { currentClub } = useClub();
  const isPilotClub = PB101_KANBAN_PILOT_CLUBS.includes(currentClub?.id);

  const isFd    = role === 'fd';
  const isMgr   = role === 'mgr' || role === 'manager';
  const isSales = role === 'sales';
  const isClub  = isFd || isMgr;

  // Role visibility per Sprint 18 nav spec:
  // sales  → SALES + CRM
  // fd/mgr → CLUB + CRM
  // mloc   → SALES + CLUB + CRM + EVENTS
  // admin  → everything
  const showSales  = isAdmin || isMloc || isSales;
  const showClub   = isAdmin || isMloc || isClub;
  // T2M and PB101 are dual-owned per the ownership model — Josh (sales) closes
  // T2M at day-10 escalation and owns PB101 follow-up for online/MS-sourced
  // leads, even though the club owns the rest of each module day-to-day.
  // Retention and Mismatch Queue stay fully club-owned, no sales overlap.
  const showSharedClub = showClub || isSales;
  const showEvents = isAdmin || isMloc;
  const showAdmin  = isAdmin;

  const linkClass = ({ isActive }) =>
    isActive ? 'nav-link active' : 'nav-link';

  return (
    <nav className="sidebar">

      {/* ── SALES ── Josh / Membership Sales */}
      {showSales && (
        <div className="sidebar-section">
          <span className="sidebar-label">Sales</span>
          <NavLink to="/sales-dashboard" className={linkClass}>📊 Sales Dashboard</NavLink>
          <NavLink to="/lead"     className={linkClass}>📋 Lead Engine</NavLink>
          <NavLink to="/lead-pipeline" className={linkClass}>📋 Lead Pipeline (New)</NavLink>
          <NavLink to="/inbound"  className={linkClass}>📥 Inbound Leads</NavLink>
          <NavLink to="/followups" className={linkClass}>🔔 Follow-Ups</NavLink>
          {(isAdmin || isSales || isMloc) && (
            <NavLink to="/commission" className={linkClass}>💵 My Commission</NavLink>
          )}
        </div>
      )}

      {/* ── CLUB ── GM + Front Desk, plus T2M/PB101 shared with Sales (Josh) */}
      {showSharedClub && (
        <div className="sidebar-section">
          <span className="sidebar-label">Club</span>
          {showClub && (
            <NavLink to="/club-dashboard" className={linkClass}>📊 Dashboard</NavLink>
          )}
          <NavLink to="/t2m"     className={linkClass}>🌱 Trial Awareness</NavLink>
          {/* T2M Kanban, Sprint 21.3 (9/28/26) — no pilot-club gate, same
              treatment as Retention Pipeline: available to everyone in
              showSharedClub (including FD, a real access gap fixed this
              build — see AzureApp/src/index.js T2M_KANBAN_ROLES). */}
          <NavLink to="/t2m-pipeline" className={linkClass}>🌱 Trial Pipeline (New)</NavLink>
          {/* Retention Kanban, Sprint 21.2 (9/24/26) — no pilot-club gate,
              unlike PB101 Pipeline below: BK wants this available to him and
              Josh (sales) from day one, not staged behind a volunteer club. */}
          <NavLink to="/retention-pipeline" className={linkClass}>🔄 Retention</NavLink>
          {showClub && (
            <NavLink to="/ambassador" className={linkClass}>🎾 {isAdmin ? 'Comps and Ambassadors' : 'Ambassadors'}</NavLink>
          )}
          <NavLink to="/pb101"   className={linkClass}>🎓 PB101</NavLink>
          {(isPilotClub || isSales) && (
            <NavLink to="/pb101-pipeline" className={linkClass}>🏓 PB101 Pipeline (New)</NavLink>
          )}
          {showClub && (
            <NavLink to="/mismatch" className={linkClass}>🚩 Mismatch Queue</NavLink>
          )}
        </div>
      )}

      {/* ── CRM ── Everyone */}
      <div className="sidebar-section">
        <span className="sidebar-label">CRM</span>
        <NavLink to="/crm/search"  className={linkClass}>🔍 Member Search</NavLink>
        <NavLink to="/crm/log"     className={linkClass}>📓 Contact Log</NavLink>
        {(isAdmin || isMloc) && (
          <NavLink to="/crm/suppression" className={linkClass}>🚫 Suppression List</NavLink>
        )}
      </div>

      {/* ── EVENTS & SPONSORSHIP ── Britt */}
      {showEvents && (
        <div className="sidebar-section">
          <span className="sidebar-label">Events &amp; Sponsorship</span>
          <NavLink to="/es-sales" className={linkClass}>🤝 Sales Pipeline</NavLink>
          <NavLink to="/activations" className={linkClass}>⚡ Activations</NavLink>
        </div>
      )}

      {/* ── ADMIN ── */}
      {showAdmin && (
        <div className="sidebar-section">
          <span className="sidebar-label">Admin</span>
          <NavLink to="/revenue"         className={linkClass}>💰 Revenue</NavLink>
          <NavLink to="/upload"          className={linkClass}>⬆️ Upload</NavLink>
          <NavLink to="/pb101-pipeline"  className={linkClass}>🏓 PB101 Pipeline</NavLink>
          <NavLink to="/admin/users"     className={linkClass}>👥 Users</NavLink>
          <NavLink to="/admin/scripts"   className={linkClass}>📝 Scripts</NavLink>
          <NavLink to="/admin/recovery"  className={linkClass}>🔄 Recovery</NavLink>
          <NavLink to="/admin/commission" className={linkClass}>💵 Commission</NavLink>
        </div>
      )}

    </nav>
  );
}
